import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Timer, Warning, Translate } from "@phosphor-icons/react";

const LETTERS = ["A", "B", "C", "D"];
const EXAM_SECONDS = 230 * 60;

export default function MockExam() {
  const { t, tDomain, tDiff, lang } = useT();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro");
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(EXAM_SECONDS);
  const [translating, setTranslating] = useState(false);
  const startedAtRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => () => intervalRef.current && clearInterval(intervalRef.current), []);

  const startExam = async () => {
    try {
      const { data } = await api.post("/quiz/start", { mode: "mock", lang });
      setQuiz(data);
      setPhase("running");
      startedAtRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(intervalRef.current);
            finish(data);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (e) {
      toast.error(t("mock.could_not_start"));
    }
  };

  const switchLanguage = async () => {
    if (!quiz) return;
    const next = (quiz.lang || "en") === "tr" ? "en" : "tr";
    setTranslating(true);
    try {
      // Translate around current question first for snappier UX (10 nearest)
      const start = Math.max(0, idx - 5);
      const end = Math.min(quiz.questions.length, idx + 5);
      const slice = quiz.questions.slice(start, end);
      const { data } = await api.post("/questions/translate", {
        question_ids: slice.map((qq) => qq.question_id),
        lang: next,
      });
      const byId = Object.fromEntries(data.questions.map((x) => [x.question_id, x]));
      setQuiz((prev) => ({
        ...prev,
        lang: next,
        questions: prev.questions.map((qq) => byId[qq.question_id] ? { ...qq, ...byId[qq.question_id] } : qq),
      }));
      // Background-translate the rest
      const rest = quiz.questions.filter((qq) => !byId[qq.question_id]).map((qq) => qq.question_id);
      if (rest.length) {
        api.post("/questions/translate", { question_ids: rest, lang: next })
          .then(({ data: d2 }) => {
            const m2 = Object.fromEntries(d2.questions.map((x) => [x.question_id, x]));
            setQuiz((prev) => ({
              ...prev,
              questions: prev.questions.map((qq) => m2[qq.question_id] ? { ...qq, ...m2[qq.question_id] } : qq),
            }));
          })
          .catch(() => {});
      }
    } catch {
      toast.error(t("common.failed"));
    } finally {
      setTranslating(false);
    }
  };

  const finish = async (q) => {
    const useQuiz = q || quiz;
    if (!useQuiz) return;
    setPhase("submitting");
    try {
      const payloadAnswers = useQuiz.questions.map((qq) => ({
        question_id: qq.question_id,
        selected_index: answers[qq.question_id] ?? null,
        is_correct: false,
        time_seconds: 0,
      }));
      const duration = Math.round((Date.now() - startedAtRef.current) / 1000);
      const { data } = await api.post("/quiz/submit", {
        attempt_id: useQuiz.attempt_id,
        answers: payloadAnswers,
        duration_seconds: duration,
      });
      navigate(`/results/${data.attempt_id}`, { replace: true });
    } catch (e) {
      toast.error(t("mock.submit_failed"));
      setPhase("running");
    }
  };

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  if (phase === "intro") {
    return (
      <AppShell>
        <div className="overline text-[#002FA7]">{t("quiz.mock_label")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("mock.title")}</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">{t("mock.subtitle")}</p>
        <div className="ai-callout mt-6 max-w-2xl"><Warning size={14} weight="fill" className="inline mr-1 text-[#D97706]" /> {t("mock.warning")}</div>
        <button onClick={startExam} className="btn-primary mt-8" data-testid="mock-start-button">{t("mock.start")}</button>
      </AppShell>
    );
  }

  if (phase === "submitting" || !quiz) {
    return (
      <AppShell>
        <div className="h-64 grid place-items-center" data-testid="mock-submitting">
          <div className="text-center">
            <div className="h-10 w-10 mx-auto rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
            <p className="mt-3 text-slate-500">{t("mock.scoring")}</p>
          </div>
        </div>
      </AppShell>
    );
  }

  const total = quiz.questions.length;
  const q = quiz.questions[idx];
  const selected = answers[q.question_id];
  const answered = Object.keys(answers).length;
  const currentLang = quiz.lang || "en";
  const switchTo = currentLang === "tr" ? "en" : "tr";

  return (
    <div className="min-h-screen bg-white">
      <header className="glass sticky top-0 z-30 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm bg-[#002FA7] grid place-items-center text-white font-display font-bold">P</div>
            <span className="font-display font-bold tracking-tight">{t("quiz.mock_label")}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={switchLanguage}
              disabled={translating}
              className="btn-secondary !py-1.5 !px-2 text-xs flex items-center gap-1"
              data-testid="mock-language-toggle"
              aria-label="Toggle question language"
              title={t("common.language")}
            >
              <Translate size={13} weight="bold" />
              <span className="font-mono font-bold">{currentLang.toUpperCase()}</span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-400 font-mono">{switchTo.toUpperCase()}</span>
            </button>
            <div className={`flex items-center gap-1 font-mono text-base ${secondsLeft < 600 ? "text-[#E11D48]" : "text-slate-900"}`} data-testid="mock-timer">
              <Timer size={18} weight="bold" /> {fmt(secondsLeft)}
            </div>
            <div className="text-sm text-slate-500"><span className="font-mono font-bold text-slate-900">{answered}</span>/{total}</div>
            <button className="btn-secondary !py-2 !px-3 text-sm" onClick={() => finish()} data-testid="mock-submit-button">{t("mock.submit")}</button>
          </div>
        </div>
        <div className="h-1 w-full bg-slate-100">
          <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="overline">{t("quiz.q_of", { n: idx + 1, total })} · {tDomain(q.domain)} · {tDiff(q.difficulty)}</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-3 leading-snug" data-testid="mock-question-text">{q.text}</h2>
        <div className="mt-6 space-y-3">
          {q.options.map((opt, i) => (
            <button
              key={i}
              className={`option-tile ${selected === i ? "selected" : ""}`}
              onClick={() => setAnswers({ ...answers, [q.question_id]: i })}
              data-testid={`mock-option-${i}`}
            >
              <span className="option-letter">{LETTERS[i]}</span>
              <span className="text-base leading-relaxed">{opt}</span>
            </button>
          ))}
        </div>
        <div className="mt-10 flex items-center justify-between gap-3">
          <button className="btn-secondary" disabled={idx === 0} onClick={() => setIdx(Math.max(0, idx - 1))} data-testid="mock-prev-button">{t("common.previous")}</button>
          {idx < total - 1 ? (
            <button className="btn-primary" onClick={() => setIdx(idx + 1)} data-testid="mock-next-button">{t("common.next")}</button>
          ) : (
            <button className="btn-primary" onClick={() => finish()} data-testid="mock-finish-button">{t("mock.finish_exam")}</button>
          )}
        </div>
      </main>
    </div>
  );
}
